import { useCallback, useEffect, useState } from 'react';

import ApiClient from 'api/axiosClient';
import close from 'assets/filters/close.svg';
import Button from 'common-ui/button';
import CustomDatePicker from 'common-ui/customDatePicker';
import Input from 'common-ui/input';
import ResetButton from 'common-ui/resetButton';
import { AsyncSelect } from 'common-ui/select';
import dayjs from 'dayjs';
import useDebounce from 'hooks/useDebounce';
import {
  AsyncSelectDiv,
  AsyncSelectWrapper,
} from 'pages/sales/leadsTable/filter/Filter.styles';

import {
  ClearAllRow,
  ClearAllText,
  CloseIconWrapper,
  CustomDatePickerWrapper,
  FilterContainer,
  FilterRow,
  Icon,
  InputWrapper,
  Line,
} from './InventoryFilter.styles';

const InventoryFilter = ({ filterData, onFilterChange, leadUuid }) => {
  const [showFilter, setShowFilter] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [fieldData, setFieldData] = useState({
    itemName: '',
    categoryName: '',
    createdBy: null,
    providedFromDate: null,
    providedToDate: null,
  });

  const loadCreatedByOptions = useCallback(
    async (searchTerm) => {
      if (!leadUuid) return [];
      try {
        const url = searchTerm
          ? `/inventory/lead/${leadUuid}/created-by-users?name=${encodeURIComponent(searchTerm)}`
          : `/inventory/lead/${leadUuid}/created-by-users`;
        const data = await ApiClient.get(url);
        return (data || []).map((user) => ({ value: user.fullName, label: user.fullName }));
      } catch {
        return [];
      }
    },
    [leadUuid]
  );

  const handleToggleFilter = () => {
    setShowFilter((prev) => !prev);
    if (!showFilter) setShowSearch(false);
  };

  const handleToggleSearch = () => {
    setShowSearch((prev) => !prev);
    if (!showSearch) setShowFilter(false);
  };

  const handleDateChange = (field, value) => {
    const updated = { ...fieldData, [field]: value };
    setFieldData(updated);
    onFilterChange(updated);
  };

  const debouncedItemName = useDebounce(fieldData.itemName, 500);
  const debouncedCategoryName = useDebounce(fieldData.categoryName, 500);

  useEffect(() => {
    onFilterChange({ ...fieldData, itemName: debouncedItemName });
  }, [debouncedItemName]);

  useEffect(() => {
    onFilterChange({ ...fieldData, categoryName: debouncedCategoryName });
  }, [debouncedCategoryName]);

  const handleItemNameChange = (value) => {
    setFieldData((prev) => ({ ...prev, itemName: value }));
  };

  const handleCategoryNameChange = (value) => {
    setFieldData((prev) => ({ ...prev, categoryName: value }));
  };

  const handleClearFilter = () => {
    const updated = {
      ...fieldData,
      providedFromDate: null,
      providedToDate: null,
      createdBy: null,
    };
    setFieldData(updated);
    onFilterChange(updated);
  };

  const handleClearSearch = () => {
    const updated = { ...fieldData, itemName: '', categoryName: '' };
    setFieldData(updated);
    onFilterChange(updated);
  };

  const handleResetAll = () => {
    const reset = {
      itemName: '',
      categoryName: '',
      createdBy: null,
      providedFromDate: null,
      providedToDate: null,
    };
    setFieldData(reset);
    onFilterChange(reset);
  };

  useEffect(() => {
    if (filterData) {
      setFieldData({
        itemName: filterData.itemName || '',
        categoryName: filterData.categoryName || '',
        createdBy: filterData.createdBy || null,
        providedFromDate: filterData.providedFromDate || null,
        providedToDate: filterData.providedToDate || null,
      });
    }
  }, [filterData]);

  const filterCount =
    !!fieldData.providedFromDate + !!fieldData.providedToDate + !!fieldData.createdBy;
  const searchCount = (fieldData.itemName ? 1 : 0) + (fieldData.categoryName ? 1 : 0);
  const hasAny = filterCount > 0 || searchCount > 0;

  return (
    <FilterContainer>
      <FilterRow style={{ alignItems: 'center' }}>
        <Button
          outlined
          onClick={handleToggleSearch}
          className="action-button"
          active={searchCount > 0}
          variant="search"
        >
          Search{searchCount > 0 ? ` (${searchCount})` : ''}
        </Button>
        <Button
          outlined
          onClick={handleToggleFilter}
          className="action-button"
          active={filterCount > 0}
          variant="filter"
        >
          Filter{filterCount > 0 ? ` (${filterCount})` : ''}
        </Button>
        {hasAny && <ResetButton onClick={handleResetAll} />}
      </FilterRow>

      {showSearch && (
        <Line>
          <ClearAllRow>
            <CloseIconWrapper onClick={handleToggleSearch}>
              <Icon src={close} />
            </CloseIconWrapper>
          </ClearAllRow>
          <FilterRow>
            <InputWrapper>
              <Input
                placeholder="Search by item name"
                value={fieldData.itemName}
                onChange={(e) => handleItemNameChange(e.target.value)}
              />
            </InputWrapper>
            <InputWrapper>
              <Input
                placeholder="Search by category name"
                value={fieldData.categoryName}
                onChange={(e) => handleCategoryNameChange(e.target.value)}
              />
            </InputWrapper>
          </FilterRow>
          <ClearAllRow>
            <ClearAllText onClick={handleClearSearch}>Clear all</ClearAllText>
          </ClearAllRow>
        </Line>
      )}

      {showFilter && (
        <Line>
          <ClearAllRow>
            <CloseIconWrapper onClick={handleToggleFilter}>
              <Icon src={close} />
            </CloseIconWrapper>
          </ClearAllRow>
          <FilterRow style={{ alignItems: 'center' }}>
            <AsyncSelectDiv>
              <AsyncSelect
                showItemsContainer={false}
                value={
                  fieldData.createdBy
                    ? { value: fieldData.createdBy, label: fieldData.createdBy }
                    : null
                }
                defaultOptions
                loadOptions={loadCreatedByOptions}
                onMenuOpen={loadCreatedByOptions}
                onChange={(option) => {
                  const updated = { ...fieldData, createdBy: option ? option.value : null };
                  setFieldData(updated);
                  onFilterChange(updated);
                }}
                placeholder="Added By"
                isClearable
                isSearchable
              />
            </AsyncSelectDiv>
            <CustomDatePickerWrapper>
              <CustomDatePicker
                value={fieldData.providedFromDate}
                onChange={(date) => handleDateChange('providedFromDate', date)}
                placeholder="Date From"
                height={44}
                maxDate={fieldData.providedToDate ? dayjs(fieldData.providedToDate) : null}
              />
            </CustomDatePickerWrapper>
            <CustomDatePickerWrapper>
              <CustomDatePicker
                value={fieldData.providedToDate}
                onChange={(date) => handleDateChange('providedToDate', date)}
                placeholder="Date To"
                height={44}
                minDate={fieldData.providedFromDate ? dayjs(fieldData.providedFromDate) : null}
              />
            </CustomDatePickerWrapper>
          </FilterRow>
          <ClearAllRow>
            <ClearAllText onClick={handleClearFilter}>Clear all</ClearAllText>
          </ClearAllRow>
        </Line>
      )}
    </FilterContainer>
  );
};

export default InventoryFilter;
